import { useState } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import ApiEndpoints from '../const/ApiEndpoints';
import SwalToast from '../components/Common/SwalToast';
import { commonSlice } from '../state/reducers/common';

export default function useOrganization() {
  const dispatch = useDispatch();
  const [organizationListData, setOrganizationListData] = useState([]);
  const [organizationListPageCount, setorganizationListPageCount] = useState(0);
  const [totalOrganizationList, setTotalOrganizationList] = useState(0);
  const [productList, setProductList] = useState([]);
  const [eventList, setEventList] = useState([]);
  const [orgDetail, setOrgDetail] = useState('');

  // CERTIFICATE

  /**
   * @function getCertificateList
   * @description getCertificateList - Retrieves details of certificate with the specified ID.
   * Sends a GET request to the certificate endpoint.
   * @param {string} id - The ID is of organization.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getCertificateList = (id) => {
    return axios
      .get(`${ApiEndpoints.BASE_URL + ApiEndpoints.CERTIFICATE_LIST_GET}`, {
        headers: {
          'X-Tenant-ID': id,
        },
      })
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function postCertificateRefresh
   * @description postCertificateRefresh - It refreshes the certificate key.
   * Sends a GET request to the certificate endpoint.
   * @param {string} id - The ID is of organization.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const postCertificateRefresh = (payload, id) =>
    axios
      .post(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.REFRESH_CERTIFICATE_KEY}`,
        payload,
        {
          headers: {
            'X-Tenant-ID': id,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Certificate key refreshed successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  // LDAP MAPPER

  /**
   * @function getMapperDetail
   * @description getMapperDetail - Retrieves details of an mapper with the specified ID.
   * Sends a GET request to the mapper detail endpoint and updates the mapper detail state
   * if the request is successful.
   * @param {string} id - The ID of the mapper to retrieve details for.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getMapperDetail = (id, ldapId, MapperId) => {
    const params = {
      ldap_id: ldapId,
      ldap_mapper_id: MapperId,
    };
    return axios
      .get(
        `${
          ApiEndpoints.BASE_URL +
          ApiEndpoints.BASE_URL_LDAP +
          id +
          ApiEndpoints.GET_LDAP_MAPPER
        }`,
        {
          params: params || {},
        }
      )
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function getApiKeyList
   * @name getApiKeyList - Fetches the list of API keys for a specified organization using the provided ID.
   * Sends a GET request to the API key list endpoint with the organization ID as a parameter.
   * @param {string} id - The ID of the organization.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getLdapMapper = (
    id,
    ldapId,
    pageIndex = 0,
    pageSize = 3,
    searchText = ''
  ) => {
    let params = {};
    if (searchText?.length !== 0 && pageIndex === 0) {
      params = {
        ldap_id: ldapId,
        search: searchText,
        page_size: pageSize,
      };
    } else {
      params = {
        ldap_id: ldapId,
        page: pageIndex + 1,
        page_size: pageSize,
        search: searchText,
      };
    }
    return axios
      .get(
        `${
          ApiEndpoints.BASE_URL +
          ApiEndpoints.BASE_URL_LDAP +
          id +
          ApiEndpoints.GET_LDAP_MAPPER
        }`,
        {
          params: params || {},
        }
      )
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function createLdapMapper
   * @description createLdapMapper - Creates a new mapper using the provided payload.
   * Sends a POST request to the API key creation endpoint and shows a success toast notification
   * if the creation is successful.
   * @param {Object} payload - The payload containing the data required to create the API key.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const createLdapMapper = (id, ldapId, payload) =>
    axios
      .post(
        `${
          ApiEndpoints.BASE_URL +
          ApiEndpoints.BASE_URL_LDAP +
          id +
          ApiEndpoints.ADD_LDAP_MAPPER
        }?ldap_id=${ldapId}`,
        payload
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'LDAP mapper created successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function deleteLdapMapper
   * @description deleteLdapMapper - Deletes an existing mapper key using the provided ID.
   * Sends a DELETE request to the mapper key deletion endpoint and shows a success toast notification
   * if the deletion is successful.
   * @param {string} id - The ID of the mapper key to delete.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const deleteLdapMapper = (id, mapperId) =>
    axios
      .delete(
        `${
          ApiEndpoints.BASE_URL +
          ApiEndpoints.BASE_URL_LDAP +
          id +
          ApiEndpoints.DELETE_LDAP_MAPPER
        }?ldap_mapper_id=${mapperId}`
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'LDAP mapper deleted successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function updateLdapMapper
   * @description updateLdapMapper - Updates an existing Api key using the provided payload and ID.
   * Sends a PATCH request to the Api key update endpoint and shows a success toast notification
   * if the update is successful.
   * @param {Object} payload - The payload containing the data required to update the Api key.
   * @param {string} id - The ID of the Api key to update.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const updateLdapMapper = (id, payload) => {
    return axios
      .put(
        `${
          ApiEndpoints.BASE_URL +
          ApiEndpoints.BASE_URL_LDAP +
          id +
          ApiEndpoints.UPDATE_LDAP_MAPPER
        }`,
        payload
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'LDAP mapper updated successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  // API KEYS
  /**
   * @function getProductService
   * @description getProductService - Retrieves details of certificate with the specified ID.
   * Sends a GET request to the certificate endpoint.
   * @param {string} id - The ID is of organization.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getProductService = (id, productId) => {
    const params = {
      product_id: productId,
    };

    return axios
      .get(`${ApiEndpoints.BASE_URL + ApiEndpoints.GET_PRODUCT_SERVICE}`, {
        headers: {
          'X-Tenant-ID': id,
        },
        params,
      })
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };
  /**
   * @function getApiKeyList
   * @name getApiKeyList - Fetches the list of API keys for a specified organization using the provided ID.
   * Sends a GET request to the API key list endpoint with the organization ID as a parameter.
   * @param {string} id - The ID of the organization.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getApiKeyList = (id, pageIndex = 0, pageSize = 3, searchText = '') => {
    let params = {};
    if (searchText?.length !== 0 && pageIndex === 0) {
      params = {
        search: searchText,
        page_size: pageSize,
      };
    } else {
      params = {
        page: pageIndex + 1,
        page_size: pageSize,
        search: searchText,
      };
    }
    return axios
      .get(`${ApiEndpoints.BASE_URL + ApiEndpoints.GET_APIKEY_LIST_API}`, {
        params: params || {},

        headers: {
          'X-Tenant-ID': id,
        },
      })
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function getApiKeyDetails
   * @name getApiKeyDetails - Fetches the Details of API keys for a specified organization using the provided ID.
   * Sends a GET request to the API key Details endpoint with the organization ID as a parameter.
   * @param {string} id - The ID of the organization.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getApiKeyDetails = (id, orgId) => {
    const params = { id };
    return axios
      .get(`${ApiEndpoints.BASE_URL + ApiEndpoints.GET_APIKEY_DETAILS}`, {
        params: params || {},
        headers: {
          'X-Tenant-ID': orgId,
        },
      })
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function createApiKey
   * @description createApiKey - Creates a new API key using the provided payload.
   * Sends a POST request to the API key creation endpoint and shows a success toast notification
   * if the creation is successful.
   * @param {Object} payload - The payload containing the data required to create the API key.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const createApiKey = (payload, id) =>
    axios
      .post(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.CREATE_APIKEY_API}`,
        payload,
        {
          headers: {
            'X-Tenant-ID': id,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'API key created successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function updateApiKey
   * @description updateApiKey - Updates an existing Api key using the provided payload and ID.
   * Sends a PATCH request to the Api key update endpoint and shows a success toast notification
   * if the update is successful.
   * @param {Object} payload - The payload containing the data required to update the Api key.
   * @param {string} id - The ID of the Api key to update.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const updateApiKey = (payload, id, orgId) =>
    axios
      .put(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.UPDATE_APIKEY_API}${id}/`,
        payload,
        {
          headers: {
            'X-Tenant-ID': orgId,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'API key updated successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function deleteApiKey
   * @description deleteApiKey - Deletes an existing Api key using the provided ID.
   * Sends a DELETE request to the Api key deletion endpoint and shows a success toast notification
   * if the deletion is successful.
   * @param {string} id - The ID of the Api key to delete.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const deleteApiKey = (id, orgId) =>
    axios
      .delete(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.DELETE_APIKEY_API}${id}/`,
        {
          headers: {
            'X-Tenant-ID': orgId,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'API key deleted successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  // PASSWORD API
  const updatePassword = (id, payload) => {
    return axios
      .put(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.BASE_URL_PASSWORD + ApiEndpoints.PUT_PASSWORD}`,
        payload,
        {
          headers: {
            'X-Tenant-ID': id,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Password policy updated successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  const getPassword = (id) => {
    return axios
      .get(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.BASE_URL_PASSWORD + ApiEndpoints.GET_PASSWORD}`,
        {
          headers: {
            'X-Tenant-ID': id,
          },
        }
      )
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  // PERMISSION API
  const getPermission = () =>
    axios.get(`${ApiEndpoints.BASE_URL + ApiEndpoints.USER_INFO}`).then(
      (res) => {
        dispatch(commonSlice.actions.setPermissions(res?.data?.clientRoles));
        dispatch(
          commonSlice.actions.setsubAdminBoolean(res?.data?.is_subadmin)
        );
        dispatch(commonSlice.actions.setUserTheme(res?.data?.theme));
        if (res?.data?.is_subadmin) {
          dispatch(commonSlice.actions.setOrg(res?.data?.tenant));
          dispatch(commonSlice.actions.setOrgName(res?.data?.tenant_name));
        }
        return res;
      },
      (error) => {
        throw error;
      }
    );

  const changeTheme = (payload) =>
    axios
      .post(`${ApiEndpoints.BASE_URL + ApiEndpoints.SAVE_THEME}`, payload)
      .then((resp) => {
        SwalToast({
          icon: 'success',
          title: 'Theme changed successfully.',
        });
        return resp.data;
      })
      .catch((error) => {
        throw error;
      });

  const createServices = (payload, id) =>
    axios
      .post(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.CREATE_PRODUCT_SERVICES}`,
        payload,
        {
          headers: {
            'X-Tenant-ID': id,
          },
        }
      )
      .then((resp) => {
        SwalToast({
          icon: 'success',
          title: 'Service created successfully.',
        });
        return resp.data;
      })
      .catch((error) => {
        throw error;
      });

  // LDAP API

  /**
   * @function createTestConnection
   * @description createTestConnection - Creates a test connection to an LDAP server using the provided payload and ID.
   * Sends a POST request to the LDAP test connection endpoint and shows a success toast notification
   * if the connection is successful.
   * @param {Object} payload - The payload containing the data required for the test connection.
   * @param {string} id - The ID of the LDAP configuration.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const createTestConnection = (payload, id) =>
    axios
      .post(
        `${
          ApiEndpoints.BASE_URL +
          ApiEndpoints.BASE_URL_LDAP +
          id +
          ApiEndpoints.POST_TEST_CONNECTION
        }`,
        payload
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Successfully connected to LDAP.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function createTestCredential
   * @description createTestCredential - Creates a test credential connection to an LDAP server using the provided payload and ID.
   * Sends a POST request to the LDAP test credential endpoint and shows a success toast notification
   * if the connection is successful.
   * @param {Object} payload - The payload containing the data required for the test credential connection.
   * @param {string} id - The ID of the LDAP configuration.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const createTestCredential = (payload, id) =>
    axios
      .post(
        `${
          ApiEndpoints.BASE_URL +
          ApiEndpoints.BASE_URL_LDAP +
          id +
          ApiEndpoints.POST_TEST_CREDENTIAL
        }`,
        payload
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Successfully connected to LDAP.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function getLDAP
   * @description getLDAP - Fetches LDAP configuration details using the provided ID.
   * Sends a GET request to the LDAP endpoint and returns the response.
   * @param {string} id - The ID of the LDAP configuration.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getLDAP = (id, ldapId) =>
    axios
      .get(
        `${
          ApiEndpoints.BASE_URL +
          ApiEndpoints.BASE_URL_LDAP +
          id +
          ApiEndpoints.GET_LDAP
        }`,
        {
          params: { ldap_id: ldapId },
        }
      )
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function getLDAPList
   * @name getLDAPList - Fetches the list of LDAP for a specified organization using the provided ID.
   * Sends a GET request to the webhook list endpoint with the organization ID as a parameter.
   * @param {string} id - The ID of the organization.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getLDAPList = (id, pageIndex = 0, pageSize = 3, searchText = '') => {
    let params = {};
    if (searchText?.length !== 0 && pageIndex === 0) {
      params = {
        organization_id: id,
        search: searchText,
        page_size: pageSize,
      };
    } else {
      params = {
        organization_id: id,
        page: pageIndex + 1,
        page_size: pageSize,
        search: searchText,
      };
    }
    return axios
      .get(
        `${
          ApiEndpoints.BASE_URL +
          ApiEndpoints.BASE_URL_LDAP +
          id +
          ApiEndpoints.GET_LDAP
        }`,
        { params: params || {} }
      )
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function postLDAP
   * @description postLDAP - Creates a new LDAP configuration using the provided ID and payload.
   * Sends a POST request to the LDAP creation endpoint and shows a success toast notification
   * if the creation is successful.
   * @param {string} id - The ID of the LDAP configuration.
   * @param {Object} payload - The payload containing the data required to create the LDAP configuration.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const postLDAP = (id, payload) =>
    axios
      .post(
        `${
          ApiEndpoints.BASE_URL +
          ApiEndpoints.BASE_URL_LDAP +
          id +
          ApiEndpoints.CREATE_LDAP
        }`,
        payload
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'User federation provider successfully created.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function postLDAP
   * @description postLDAP - Creates a new LDAP configuration using the provided ID and payload.
   * Sends a POST request to the LDAP creation endpoint and shows a success toast notification
   * if the creation is successful.
   * @param {string} id - The ID of the LDAP configuration.
   * @param {Object} payload - The payload containing the data required to create the LDAP configuration.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const deleteLDAP = (id, ldapId) =>
    axios
      .delete(
        `${
          ApiEndpoints.BASE_URL +
          ApiEndpoints.BASE_URL_LDAP +
          id +
          ApiEndpoints.DELETE_LDAP
        }`,
        {
          data: {
            id: ldapId,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'LDAP deleted successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function actionLDAP
   * @description actionLDAP - Executes a specific action on an LDAP configuration using the provided ID and payload.
   * Sends a POST request to the LDAP action endpoint and shows a success toast notification
   * if the action is successful.
   * @param {string} id - The ID of the LDAP configuration.
   * @param {Object} payload - The payload containing the data required to perform the action on the LDAP configuration.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const actionLDAP = (id, payload) =>
    axios
      .post(
        `${
          ApiEndpoints.BASE_URL +
          ApiEndpoints.BASE_URL_LDAP +
          id +
          ApiEndpoints.LDAP_ACTION
        }`,
        payload
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: "LDAP's action successfully.",
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function updateLDAP
   * @description updateLDAP - Updates an existing LDAP configuration using the provided ID and payload.
   * Sends a PUT request to the LDAP update endpoint and shows a success toast notification
   * if the update is successful.
   * @param {string} id - The ID of the LDAP configuration.
   * @param {Object} payload - The payload containing the data required to update the LDAP configuration.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const updateLDAP = (id, payload) =>
    axios
      .put(
        `${
          ApiEndpoints.BASE_URL +
          ApiEndpoints.BASE_URL_LDAP +
          id +
          ApiEndpoints.UPDATE_LDAP
        }`,
        payload
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'User federation provider successfully saved.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  const changeStatusLDAP = (orgName, payload) => {
    return axios
      .put(
        `${
          ApiEndpoints.BASE_URL +
          ApiEndpoints.BASE_URL_LDAP +
          orgName +
          ApiEndpoints.STATUS_LDAP
        }`,
        payload
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Successfully changed LDAP status.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  // WEBHOOK API

  /**
   * @function getWebHookList
   * @name getWebHookList - Fetches the list of webhooks for a specified organization using the provided ID.
   * Sends a GET request to the webhook list endpoint with the organization ID as a parameter.
   * @param {string} id - The ID of the organization.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getWebHookList = (id, pageIndex = 0, pageSize = 3, searchText = '') => {
    let params = {};
    if (searchText?.length !== 0 && pageIndex === 0) {
      params = {
        organization_id: id,
        search: searchText,
        page_size: pageSize,
      };
    } else {
      params = {
        organization_id: id,
        page: pageIndex + 1,
        page_size: pageSize,
        search: searchText,
      };
    }
    return axios
      .get(`${ApiEndpoints.BASE_URL + ApiEndpoints.GET_WEBHOOK_LIST_API}`, {
        params: params || {},
      })
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function createWebHook
   * @description createWebHook - Creates a new webhook using the provided payload.
   * Sends a POST request to the webhook creation endpoint and shows a success toast notification
   * if the creation is successful.
   * @param {Object} payload - The payload containing the data required to create the webhook.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const createWebHook = (payload) =>
    axios
      .post(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.CREATE_WEBHOOK_API}`,
        payload
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Webhook created successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function updateWebHook
   * @description updateWebHook - Updates an existing webhook using the provided payload and ID.
   * Sends a PATCH request to the webhook update endpoint and shows a success toast notification
   * if the update is successful.
   * @param {Object} payload - The payload containing the data required to update the webhook.
   * @param {string} id - The ID of the webhook to update.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const updateWebHook = (payload, id) =>
    axios
      .patch(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.UPDATE_WEBHOOK_API + id}/`,
        payload
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Webhook updated successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function deleteWebHook
   * @description deleteWebHook - Deletes an existing webhook using the provided ID.
   * Sends a DELETE request to the webhook deletion endpoint and shows a success toast notification
   * if the deletion is successful.
   * @param {string} id - The ID of the webhook to delete.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const deleteWebHook = (id) =>
    axios
      .delete(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.CREATE_WEBHOOK_API + id}/`
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Webhook deleted successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function getEventOfWebhook
   * @description getEventOfWebhook - Fetches the list of events for webhooks.
   * Sends a GET request to the webhook events endpoint and updates the event list state
   * if the request is successful.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getEventOfWebhook = () =>
    axios
      .get(`${ApiEndpoints.BASE_URL + ApiEndpoints.GET_WEBHOOK_EVENT_API}`)
      .then(
        (res) => {
          setEventList(res?.data);
          return res;
        },
        (error) => {
          throw error;
        }
      );

  // GROUPLIST
  /**
   * @function syncUsersToAllTheProduct
   * @description syncUsersToAllTheProduct - Sync all the users to all the available products.
   * Sends a GET request to the certificate endpoint.
   * @param {string} id - The ID is of organization.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const syncUsersToAllTheProduct = (id) => {
    return axios
      .get(`${ApiEndpoints.BASE_URL + ApiEndpoints.SYNC_USERS}`, {
        headers: {
          'X-Tenant-ID': id,
        },
      })
      .then(
        (res) => {
          console.log(res);
          SwalToast({
            icon: 'success',
            title: res?.data?.message,
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };
  /**
   * @function getGroupList
   * @description getGroupList - Fetches the list of groups for a specified organization using the provided ID.
   * Sends a GET request to the group list endpoint with the organization ID as a parameter.
   * @param {string} id - The ID of the organization.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getGroupList = (id, clientId) => {
    return axios
      .get(`${ApiEndpoints.BASE_URL + ApiEndpoints.GET_GROUP_LIST}`, {
        params: {
          product_id: clientId,
        },
        headers: {
          'X-Tenant-ID': id,
        },
      })
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function createGroup
   * @description createGroup - Creates a new group using the provided payload.
   * Sends a POST request to the group creation endpoint and shows a success toast notification
   * if the creation is successful.
   * @param {Object} payload - The payload containing the data required to create the group.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const createGroup = (payload, orgId) =>
    axios
      .post(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.CREATE_GROUP_LIST}`,
        payload,
        {
          headers: {
            'X-Tenant-ID': orgId,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Group created successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function updateGroup
   * @description updateGroup - Updates an existing group using the provided payload and ID.
   * Sends a PATCH request to the group update endpoint and shows a success toast notification
   * if the update is successful.
   * @param {Object} payload - The payload containing the data required to update the group.
   * @param {string} id - The ID of the group to update.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const updateGroup = (payload, id, orgId) =>
    axios
      .put(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.UPDATE_PERMISSION}${id}/`,
        payload,
        {
          headers: {
            'X-Tenant-ID': orgId,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Group data updated successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function groupDelete
   * @description groupDelete - Deletes a user with the specified ID from the organization with the given organization ID.
   * Sends a DELETE request to the user deletion endpoint and shows a success toast notification
   * if the deletion is successful.
   * @param {string} id - The ID of the user to delete.
   * @param {string} org_ID - The ID of the organization from which to delete the user.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const groupDelete = (id, orgId) =>
    axios
      .delete(`${ApiEndpoints.BASE_URL + ApiEndpoints.DELETE_GROUP}${id}/`, {
        headers: {
          'X-Tenant-ID': orgId,
        },
      })
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Group deleted successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function getMemberList
   * @description getMemberList - Fetches the list of members for a specified group within an organization.
   * Sends a GET request to the member list endpoint with the provided parameters.
   * @param {string} group_id - The ID of the group for which to fetch members.
   * @param {string} user_details - Details of the users to include in the member list.
   * @param {string} organization_id - The ID of the organization to which the group belongs.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getMemberList = (
    groupId,
    orgName,
    pageIndex = 0,
    pageSize = 5,
    searchText = ''
  ) => {
    let params = {};
    if (searchText?.length !== 0 && pageIndex === 0) {
      params = {
        search: searchText,
        page_size: pageSize,
      };
    } else {
      params = {
        page: pageIndex + 1,
        page_size: pageSize,
        search: searchText,
      };
    }
    return axios
      .get(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.GET_MEMBER_LIST}/${groupId}`,
        {
          params: params || {},
          headers: {
            'X-Tenant-ID': orgName,
          },
        }
      )
      .then((res) => {
        return res;
      })
      .catch((error) => {
        console.error('Error fetching member list:', error);
        return error;
      });
  };

  /**
   * @function getDefaultPermission
   * @description getDefaultPermission - Fetches the default permissions.
   * Sends a GET request to the default permission endpoint.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getDefaultPermission = (clientId, id) => {
    return axios
      .get(`${ApiEndpoints.BASE_URL + ApiEndpoints.GET_DEFAULT_PERMISSION}`, {
        params: {
          product_id: clientId,
        },

        headers: {
          'X-Tenant-ID': id,
        },
      })
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function setDefaultPermission
   * @description setDefaultPermission -set the default permissions.
   * Sends a patch request to the default permission endpoint.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const setDefaultPermission = (payload, orgId) => {
    return axios
      .put(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.GET_DEFAULT_PERMISSION}`,
        payload,
        {
          headers: {
            'X-Tenant-ID': orgId,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Custom permission updated successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };
  // APPLICATION DETAILS API

  /**
   * @function getFileConfig
   * @description getFileConfig - Fetches the file configuration for a specified product.
   * Sends a GET request to the file configuration endpoint with the product ID as a parameter.
   * @param {string} productId - The ID of the product for which to fetch the file configuration.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getFileConfig = (productId, orgId, hideLoader) => {
    return axios
      .get(`${ApiEndpoints.BASE_URL + ApiEndpoints.GET_FILE_CONFIG}`, {
        params: {
          product: productId,
        },
        headers: { 'X-Tenant-ID': orgId },
        loader: hideLoader,
      })
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };
  const getFileDBConfig = (productId, orgId, hideLoader) => {
    return axios
      .post(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.GET_DB_CONFIG}`,
        {
          product_id: productId,
        },
        {
          headers: { 'X-Tenant-ID': orgId },
          loader: hideLoader,
        }
      )
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  const editFileConfig = (payload, orgId) => {
    return axios
      .put(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.EDIT_FILE_CONFIG}`,
        payload,
        {
          headers: { 'X-Tenant-ID': orgId },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'File configuration updated successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function getApplicationList
   * @description getApplicationList - Fetches the list of applications for a specified organization using the provided ID.
   * Sends a GET request to the application list endpoint with the organization ID as a parameter.
   * @param {string} id - The ID of the organization.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getApplicationList = (
    id,
    pageIndex = '',
    pageSize = '',
    searchText = ''
  ) => {
    let params = {};
    if (searchText?.length !== 0 && pageIndex === 0) {
      params = {
        search: searchText,
        page_size: pageSize,
      };
    } else {
      params = {
        page: pageIndex === '' ? '' : pageIndex + 1,
        page_size: pageSize,
        search: searchText,
      };
    }
    return axios
      .get(`${ApiEndpoints.BASE_URL + ApiEndpoints.GET_APPLICATION_LIST}`, {
        params: params || {},

        headers: {
          'X-Tenant-ID': id,
        },
      })
      .then(
        (res) => {
          return res?.data;
        },
        (error) => {
          throw error;
        }
      );
  };

  const getCodeDetail = (id) => {
    const params = {
      client_id: id,
    };
    return axios
      .get(`${ApiEndpoints.BASE_URL + ApiEndpoints.GET_CODE}`, {
        params: params || {},
      })
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  const postCodeDetail = (payload) => {
    axios
      .post(`${ApiEndpoints.BASE_URL + ApiEndpoints.POST_CODE}`, payload)
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'File configuration updated successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function updateApplication
   * @description updateApplication -  Updates an existing application using the provided payload and ID.
   * Sends a PATCH request to the application update endpoint and shows a success toast notification
   * if the update is successful.
   * @param {Object} payload - The payload containing the data required to update the application.
   * @param {string} id - The ID of the application to update.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const updateApplication = (payload, id, orgId) =>
    axios
      .put(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.PATCH_APPLICATION + id}/`,
        payload,
        {
          headers: {
            'X-Tenant-ID': orgId,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Application status updated successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function ApplicationDelete
   * @description ApplicationDelete - Deletes an application with the specified ID.
   * Sends a DELETE request to the application deletion endpoint and shows a success toast notification
   * if the deletion is successful.
   * @param {string} id - The ID of the application to delete.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const ApplicationDelete = (id, orgId) =>
    axios
      .delete(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.DELETE_APPLICATION + id}/`,
        {
          headers: {
            'X-Tenant-ID': orgId,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Application deleted successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function createApplication
   * @description createApplication - Creates a new application using the provided payload.
   * Sends a POST request to the application creation endpoint and shows a success toast notification
   * if the creation is successful.
   * @param {Object} payload - The payload containing the data required to create the application.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const createApplication = (payload, id) =>
    axios
      .post(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.POST_APPLICATION}`,
        payload,
        {
          headers: {
            'X-Tenant-ID': id,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Application created successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function getProductList
   * @description getProductList - Fetches the list of products.
   * Sends a GET request to the product list endpoint and sets the product list state with the received data.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getProductList = (id) =>
    axios
      .get(`${ApiEndpoints.BASE_URL + ApiEndpoints.GET_PRODUCT_LIST}`, {
        headers: {
          'X-Tenant-ID': id,
        },
      })
      .then(
        (res) => {
          setProductList(res?.data);
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function createProductList
   * @description createProductList - Creates a new product using the provided payload.
   * Sends a POST request to the product creation endpoint, fetches the updated product list,
   * and shows a success toast notification if the creation is successful.
   * @param {Object} payload - The payload containing the data required to create the product.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const createProductList = (payload) =>
    axios
      .post(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.POST_PRODUCT_LIST}`,
        payload
      )
      .then(
        (res) => {
          getProductList();
          SwalToast({
            icon: 'success',
            title: 'Product created successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  // ACTIVE DIRECTORY API

  /**
   * @function updateActiveDirectory
   * @description updateActiveDirectory - Updates an existing Active Directory configuration with the provided ID and payload.
   * Sends a PUT request to the Active Directory update endpoint and shows a success toast notification
   * if the update is successful.
   * @param {string} id - The ID of the Active Directory configuration to update.
   * @param {Object} payload - The payload containing the data required to update the Active Directory configuration.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const updateActiveDirectory = (id, payload) =>
    axios
      .put(
        `${
          ApiEndpoints.BASE_URL +
          ApiEndpoints.BASE_URL_ACTIVE_DIRECTORY +
          id +
          ApiEndpoints.PUT_ACTIVE_DIRECTORY
        }`,
        payload
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Active directory updated successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function deleteActiveDirectory
   * @description deleteActiveDirectory - Deletes an Active Directory configuration with the specified ID and name.
   * Sends a DELETE request to the Active Directory deletion endpoint and shows a success toast notification
   * if the deletion is successful.
   * @param {string} id - The ID of the Active Directory configuration to delete.
   * @param {string} activeDirName - The name of the Active Directory configuration to delete.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const deleteActiveDirectory = (id, activeDirName) =>
    axios
      .delete(
        `${
          ApiEndpoints.BASE_URL +
          ApiEndpoints.BASE_URL_ACTIVE_DIRECTORY +
          id +
          ApiEndpoints.DELETE_ACTIVE_DIRECTORY
        }`,
        {
          data: {
            alias: activeDirName,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Active directory deleted successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function getActiveDirectory
   * @description getActiveDirectory - Retrieves information about an Active Directory configuration with the specified ID.
   * Sends a GET request to the Active Directory endpoint and returns the response.
   * @param {string} id - The ID of the Active Directory configuration to retrieve.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getActiveDirectory = (id) =>
    axios
      .get(
        `${
          ApiEndpoints.BASE_URL +
          ApiEndpoints.BASE_URL_ACTIVE_DIRECTORY +
          id +
          ApiEndpoints.GET_ACTIVE_DIRECTORY
        }`
      )
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function createActiveDirectory
   * @description createActiveDirectory - Creates a new Active Directory configuration with the provided payload and organization ID.
   * Sends a POST request to the Active Directory creation endpoint and shows a success toast notification
   * if the creation is successful.
   * @param {Object} payload - The payload containing the data required to create the Active Directory configuration.
   * @param {string} id - The ID of the organization associated with the Active Directory configuration.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const createActiveDirectory = (payload, id) =>
    axios
      .post(
        `${
          ApiEndpoints.BASE_URL +
          ApiEndpoints.BASE_URL_ACTIVE_DIRECTORY +
          id +
          ApiEndpoints.CREATE_ACTIVE_DIRECTORY
        }`,
        payload
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Active Directory created successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  // LOGIN RELATED API

  /**
   * @function getLogin
   * @description getLogin - Retrieves login information with the specified ID.
   * Sends a GET request to the login endpoint and returns the response.
   * @param {string} id - The ID of the login information to retrieve.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getLogin = (id) =>
    axios
      .get(
        `${
          ApiEndpoints.BASE_URL +
          ApiEndpoints.LOGIN_BASE_URL +
          ApiEndpoints.LOGIN_GET
        }`,
        {
          headers: {
            'X-Tenant-ID': id,
          },
        }
      )
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );

  /**
   * @function updateLogin
   * @description updateLogin
   * @param {string} id - The ID of the login information to update.
   * @param {Object} payload - The payload containing the data required to update the login information.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const updateLogin = (id, payload) =>
    axios
      .put(
        `${
          ApiEndpoints.BASE_URL +
          ApiEndpoints.LOGIN_BASE_URL +
          ApiEndpoints.LOGIN_PUT
        }`,
        payload,
        {
          headers: {
            'X-Tenant-ID': id,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Login updated successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  // ORGANIZATION RELATED API

  /**
   * @function organizationDetail
   * @description organizationDetail - Retrieves details of an organization with the specified ID.
   * Sends a GET request to the organization detail endpoint and updates the organization detail state
   * if the request is successful.
   * @param {string} id - The ID of the organization to retrieve details for.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const organizationDetail = (id) =>
    axios.get(`${ApiEndpoints.BASE_URL + ApiEndpoints.TENANTS_PUT + id}`).then(
      (res) => {
        setOrgDetail(res?.data);
        return res;
      },
      (error) => {
        throw error;
      }
    );

  /**
   * @function organizationDelete
   * @description organizationDelete - Deletes an organization with the specified ID.
   * Sends a DELETE request to the organization deletion endpoint and shows a success toast notification
   * if the deletion is successful.
   * @param {string} id - The ID of the organization to delete.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const organizationDelete = (id) =>
    axios
      .delete(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.ORGANIZATION_DELETE + id}/`
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Organization deleted successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function organizationDetailsList
   * @description organizationDetailsList - Retrieves details of all organizations.
   * Sends a GET request to the organization details endpoint and updates the organization list state
   * if the request is successful.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const organizationDetailsList = (
    pageIndex = '',
    pageSize = '',
    searchText = ''
  ) => {
    let params = {};
    if (searchText?.length !== 0 && pageIndex === 0) {
      params = {
        search: searchText,
        page_size: pageSize,
      };
    } else {
      params = {
        search: searchText,
        page: pageIndex === '' ? '' : pageIndex + 1,
        page_size: pageSize,
      };
    }
    return axios
      .get(`${ApiEndpoints.BASE_URL + ApiEndpoints.TENANTS_GET}`, {
        params: params || {},
      })
      .then(
        (res) => {
          setOrganizationListData(res?.data?.data);
          setorganizationListPageCount(res?.data?.max_pages);
          setTotalOrganizationList(res?.data?.total_count);
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function updateOrganization
   * @description updateOrganization - Updates an organization with the specified ID and payload.
   * Sends a PATCH request to the organization update endpoint and shows a success toast notification
   * if the update is successful. It also updates the organization ID in the Redux store.
   * @param {Object} payload - The payload containing the data required to update the organization.
   * @param {string} id - The ID of the organization to update.
   * @returns {Promise<Object>} A promise that resolves with the updated organization data or rejects with an error.
   */
  const updateOrganization = (payload, id) =>
    axios
      .put(`${ApiEndpoints.BASE_URL + ApiEndpoints.TENANTS_PUT + id}/`, payload)
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Organization updated successfully.',
          });
          return res?.data;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function createOrganization
   * @description createOrganization - Creates a new organization with the provided payload.
   * Sends a POST request to the organization creation endpoint and shows a success toast notification
   * if the creation is successful. It also updates the organization ID in the Redux store.
   * @param {Object} payload - The payload containing the data required to create the organization.
   * @returns {Promise<Object>} A promise that resolves with the created organization data or rejects with an error.
   */
  const createOrganization = (payload) => {
    return axios
      .post(`${ApiEndpoints.BASE_URL}${ApiEndpoints.TENANTS_POST}`, payload)
      .then((resp) => {
        SwalToast({
          icon: 'success',
          title: 'Organization created successfully.',
        });
        return resp;
      })
      .catch((error) => {
        throw error;
      });
  };

  /**
   * @function getEmailRequest
   * @description getEmailRequest - Retrieves email requests for an organization with the specified ID.
   * Sends a GET request to the organization email requests endpoint.
   * @param {string} id - The ID of the organization to retrieve email requests for.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getEmailRequest = (id) =>
    axios
      .get(`${ApiEndpoints.BASE_URL + ApiEndpoints.ORGANIZATION_EMAIL_GET}`, {
        headers: {
          'X-Tenant-ID': id,
        },
      })
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );

  /**
   * @function createEmailRequest
   * @description createEmailRequest - Creates an email request with the provided payload.
   * Sends a POST request to the organization email request creation endpoint and shows a success toast notification
   * if the creation is successful.
   * @param {Object} payload - The payload containing the data required to create the email request.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const createEmailRequest = (id, payload) =>
    axios
      .post(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.ORGANIZATION_EMAIL_POST}`,
        payload,
        {
          headers: {
            'X-Tenant-ID': id,
          },
        }
      )
      .then(
        (resp) => {
          SwalToast({
            icon: 'success',
            title: 'Email request created successfully.',
          });
          return resp;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function updateEmailRequest
   * @description updateEmailRequest - Updates an email request with the specified ID and payload.
   * Sends a PATCH request to the organization email request update endpoint and shows a success toast notification
   * if the update is successful.
   * @param {Object} payload - The payload containing the data required to update the email request.
   * @param {string} id - The ID of the email request to update.
   * @returns {Promise<Object>} A promise that resolves with the updated email request data or rejects with an error.
   */
  const updateEmailRequest = (id, payload) =>
    axios
      .put(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.ORGANIZATION_EMAIL_PATCH}`,
        payload,
        {
          headers: {
            'X-Tenant-ID': id,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Email updated successfully.',
          });
          return res?.data;
        },
        (error) => {
          throw error;
        }
      );

  const uploadBulkUser = (orgName, file) => {
    const formData = new FormData();
    formData.append('organization_id', orgName);
    formData.append('csv_file', file);

    return axios
      .post(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.ORGANIZATION_UPLOADFILE_POST}`,
        formData,
        {
          headers: {},
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Successfully uploaded bulk user.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function getIdentityProviderList
   * @name getIdentityProviderList - Fetches the list of Indentity Provider for a specified organization using the provided ID.
   * Sends a GET request to the webhook list endpoint with the organization ID as a parameter.
   * @param {string} id - The ID of the organization.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getIdentityProviderList = (
    id,
    pageIndex = 0,
    pageSize = 3,
    searchText = ''
  ) => {
    let params = {};
    if (searchText?.length !== 0 && pageIndex === 0) {
      params = {
        search: searchText,
        page_size: pageSize,
      };
    } else {
      params = {
        page: pageIndex + 1,
        page_size: pageSize,
        search: searchText,
      };
    }
    return axios
      .get(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.IDENTITY_PROVIDER_LIST_GET}`,
        {
          headers: {
            'X-Tenant-ID': id,
          },
          params: params || {},
        }
      )
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function getIdentityProvider
   * @name getIdentityProvider - Fetches the Indentity Provider for a specified organization using the provided alias.
   * Sends a GET request to the Identity Provider endpoint with the organization ID as a parameter.
   * @param {string} id - The ID of the organization.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getIdentityProvider = (id, aliasName) => {
    return axios
      .get(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.IDENTITY_PROVIDER_LIST_GET}`,
        {
          headers: {
            'X-Tenant-ID': id,
          },
          params: {
            alias: aliasName,
          },
        }
      )
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function createIdentityProvider
   * @description postLDAP - Creates a new Indentity Provider configuration using the provided ID and payload.
   * Sends a POST request to the Identity Provider creation endpoint and shows a success toast notification
   * if the creation is successful.
   * @param {string} id - The ID of the Identity Provider configuration.
   * @param {Object} payload - The payload containing the data required to create the Identity Provider configuration.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const createIdentityProviderSaml = (id, payload) =>
    axios
      .post(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.IDENTITY_PROVIDER_SAML_POST}`,
        payload,
        {
          headers: {
            'X-Tenant-ID': id,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Identity provider created successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function changeStatusIdentityProvider
   * @description put Identity Provider changes the status of the Identity Provider.
   * @param {string} id - The ID of the Identity Provider configuration.
   * @param {Object} payload - The payload containing the data required to change the status of Identity Provider.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const changeStatusIdentityProvider = (id, payload) =>
    axios
      .put(
        `${
          ApiEndpoints.BASE_URL +
          ApiEndpoints.ORGANIZATION_IDENTITY_PROVIDER +
          id +
          ApiEndpoints.ORGANIZATION_IDENTITY_PROVIDER_CHANGE_STATUS
        }`,
        payload
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Identity provider status changed successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function deleteIdentityProvider
   * @description post deleteIdentityProvider - Delete IdentityProvider configuration using the provided ID and payload.
   * Sends a POST request to the IdentityProvider deletion endpoint and shows a success toast notification
   * if the deletion is successful.
   * @param {string} id - The ID of the IdentityProvider configuration.
   * @param {Object} payload - The payload containing the data required to delete the IdentityProvider configuration.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const deleteIdentityProvider = (id, payload) => {
    return axios
      .delete(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.IDENTITY_PROVIDER_DELETE}`,
        {
          data: payload,
          headers: {
            'X-Tenant-ID': id,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Identity provider deleted successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function updateIdentityProvider
   * @description put updateIdentityProvider - Update Identity Provider configuration using the provided ID and payload.
   * Sends a PUT request to the IdentityProvider updatation endpoint and shows a success toast notification
   * if the deletion is successful.
   * @param {string} id - The ID of the IdentityProvider configuration.
   * @param {Object} payload - The payload containing the data required to update the IdentityProvider configuration.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const updateIdentityProviderSaml = (id, payload) =>
    axios
      .put(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.IDENTITY_PROVIDER_UPDATE_SAML}`,
        payload,
        {
          headers: {
            'X-Tenant-ID': id,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Identity provider updated successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function updateIdentityProvider
   * @description put updateIdentityProvider - Update Identity Provider configuration using the provided ID and payload.
   * Sends a PUT request to the IdentityProvider updatation endpoint and shows a success toast notification
   * if the deletion is successful.
   * @param {string} id - The ID of the IdentityProvider configuration.
   * @param {Object} payload - The payload containing the data required to update the IdentityProvider configuration.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const updateIdentityProviderOidc = (id, payload) =>
    axios
      .put(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.IDENTITY_PROVIDER_UPDATE_OIDC}`,
        payload,
        {
          headers: {
            'X-Tenant-ID': id,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Identity provider updated successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function createIdentityProvider
   * @description postLDAP - Creates a new Indentity Provider configuration using the provided ID and payload.
   * Sends a POST request to the Identity Provider creation endpoint and shows a success toast notification
   * if the creation is successful.
   * @param {string} id - The ID of the Identity Provider configuration.
   * @param {Object} payload - The payload containing the data required to create the Identity Provider configuration.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const createIdentityProviderOidc = (id, payload) =>
    axios
      .post(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.IDENTITY_PROVIDER_OIDC_POST}`,
        payload,
        {
          headers: {
            'X-Tenant-ID': id,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Identity provider created successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  // IDP MAPPING API

  /**
   * @function getIdpMappingList
   * @description getIdpMappingList - Fetches the list of IDP mappings for a specified organization.
   * Sends a GET request to the IDP mapping endpoint with the organization ID as a header.
   * @param {string} id - The ID of the organization.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getIdpMappingList = (tenantId) => {
    return axios
      .get(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.IDENTITY_PROVIDER_MAPPING}`,
        {
          headers: {
            'X-Tenant-ID': tenantId,
          },
        }
      )
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function createIdpMapping
   * @description createIdpMapping - Creates a new IDP mapping using the provided payload and organization ID.
   * Sends a POST request to the IDP mapping creation endpoint and shows a success toast notification
   * if the creation is successful.
   * @param {string} id - The ID of the organization.
   * @param {Object} payload - The payload containing the data required to create the IDP mapping.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const createIdpMapping = (tenantId, payload) =>
    axios
      .post(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.IDENTITY_PROVIDER_MAPPING}`,
        payload,
        {
          headers: {
            'X-Tenant-ID': tenantId,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'IDP mapping created successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function updateIdpMapping
   * @description updateIdpMapping - Updates an existing IDP mapping using the provided payload, mapping ID and organization ID.
   * Sends a PUT request to the IDP mapping update endpoint and shows a success toast notification
   * if the update is successful.
   * @param {string} orgName - The ID of the organization.
   * @param {string} mappingId - The ID of the IDP mapping to update.
   * @param {Object} payload - The payload containing the data required to update the IDP mapping.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const updateIdpMapping = (tenantId, mappingId, payload) =>
    axios
      .put(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.IDENTITY_PROVIDER_MAPPING}${mappingId}/`,
        payload,
        {
          headers: {
            'X-Tenant-ID': tenantId,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'IDP mapping updated successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /** delete IDP mapping
   * @param {string} tenantId - The ID of the tenant.
   * @param {string} mappingId - The ID of the IDP mapping to delete.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const deleteIdpMapping = (tenantId, mappingId) =>
    axios
      .delete(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.IDENTITY_PROVIDER_MAPPING}${mappingId}/`,
        {
          headers: {
            'X-Tenant-ID': tenantId,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'IDP mapping deleted successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  // CLAIM MAPPING API

  /**
   * @function getClaimMappingList
   * @description getClaimMappingList - Fetches the list of claim mappings for a specified organization and provider type.
   * Sends a GET request to the claim mapping endpoint with the organization ID as a header and provider type as query param.
   * @param {string} id - The ID of the organization.
   * @param {boolean} isSaml - Whether the provider is SAML or OIDC.
   * @param {number} pageIndex - The page index for pagination (default: 0).
   * @param {number} pageSize - The page size for pagination (default: 5).
   * @param {string} searchText - The search text for filtering (default: '').
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const getClaimMappingList = (
    id,
    isSaml = false,
    pageIndex = 0,
    pageSize = 5,
    searchText = ''
  ) => {
    let params = {};

    params = {
      ...(isSaml ? { is_saml: 'True' } : {}),
      page: pageIndex + 1,
      page_size: pageSize,
      search: searchText,
    };

    return axios
      .get(`${ApiEndpoints.BASE_URL + ApiEndpoints.CLAIM_MAPPING}`, {
        headers: {
          'X-Tenant-ID': id,
        },
        params: params || {},
      })
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function createClaimMapping
   * @description createClaimMapping - Creates a new claim mapping using the provided payload and organization ID.
   * Sends a POST request to the claim mapping creation endpoint and shows a success toast notification
   * if the creation is successful.
   * @param {string} id - The ID of the organization.
   * @param {Object} payload - The payload containing the data required to create the claim mapping.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const createClaimMapping = (orgId, payload) =>
    axios
      .post(`${ApiEndpoints.BASE_URL + ApiEndpoints.CLAIM_MAPPING}`, payload, {
        headers: {
          'X-Tenant-ID': orgId,
        },
      })
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Claim mapping created successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function updateClaimMapping
   * @description updateClaimMapping - Updates an existing claim mapping using the provided payload, mapping ID and organization ID.
   * Sends a PUT request to the claim mapping update endpoint and shows a success toast notification
   * if the update is successful.
   * @param {string} orgName - The ID of the organization.
   * @param {string} mappingId - The ID of the claim mapping to update.
   * @param {Object} payload - The payload containing the data required to update the claim mapping.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const updateClaimMapping = (orgName, isSaml, mappingId, payload) =>
    axios
      .put(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.CLAIM_MAPPING}${mappingId}/`,
        payload,
        {
          headers: {
            'X-Tenant-ID': orgName,
          },
          params: {
            ...(isSaml ? { is_saml: 'True' } : {}),
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Claim mapping updated successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function deleteClaimMapping
   * @description Deletes a claim mapping using the provided organization ID and mapping ID.
   * Sends a DELETE request to the claim mapping deletion endpoint and shows a success toast notification
   * if the deletion is successful.
   * @param {string} orgName - The ID of the organization.
   * @param {boolean} isSaml - Indicates if the IDP is SAML.
   * @param {string} mappingId - The ID of the claim mapping to delete.
   * @returns {Promise<Object>} A promise that resolves with the response from the server or rejects with an error.
   */
  const deleteClaimMapping = (orgName, isSaml, mappingId, data) =>
    axios
      .delete(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.CLAIM_MAPPING}${mappingId}/`,
        {
          headers: {
            'X-Tenant-ID': orgName,
          },
          params: {
            ...(isSaml ? { is_saml: 'True' } : {}),
          },
          data,
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Claim mapping deleted successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  return {
    organizationListPageCount,
    totalOrganizationList,
    orgDetail,
    changeStatusLDAP,
    setOrgDetail,
    organizationDetailsList,
    organizationDetail,
    createOrganization,
    updateOrganization,
    organizationDelete,
    getIdentityProvider,
    getIdentityProviderList,
    changeStatusIdentityProvider,
    deleteIdentityProvider,
    updateLogin,
    createActiveDirectory,
    getLogin,
    getActiveDirectory,
    updateActiveDirectory,
    deleteActiveDirectory,
    getEmailRequest,
    createEmailRequest,
    updateEmailRequest,
    getApplicationList,
    updateApplication,
    ApplicationDelete,
    productList,
    getProductList,
    createProductList,
    createApplication,
    getWebHookList,
    // webhookList,
    getGroupList,
    createGroup,
    updateGroup,
    groupDelete,
    getMemberList,
    getDefaultPermission,
    createWebHook,
    updateWebHook,
    deleteWebHook,
    eventList,
    getEventOfWebhook,
    createTestConnection,
    createTestCredential,
    createIdentityProviderSaml,
    updateIdentityProviderSaml,
    createIdentityProviderOidc,
    updateIdentityProviderOidc,
    getLDAP,
    getLDAPList,
    postLDAP,
    actionLDAP,
    updateLDAP,
    getPermission,
    uploadBulkUser,
    setDefaultPermission,
    updatePassword,
    getPassword,
    getApiKeyDetails,
    getApiKeyList,
    createApiKey,
    updateApiKey,
    deleteApiKey,
    getCodeDetail,
    postCodeDetail,
    deleteLDAP,
    getLdapMapper,
    createLdapMapper,
    deleteLdapMapper,
    getMapperDetail,
    updateLdapMapper,
    changeTheme,
    getCertificateList,
    postCertificateRefresh,
    getIdpMappingList,
    createIdpMapping,
    updateIdpMapping,
    deleteIdpMapping,
    getClaimMappingList,
    createClaimMapping,
    updateClaimMapping,
    organizationListData,
    getProductService,
    deleteClaimMapping,
    getFileConfig,
    editFileConfig,
    createServices,
    getFileDBConfig,
    syncUsersToAllTheProduct,
  };
}
